/*
 * Thank-you page runtime for EasySell COD orders.
 *
 * EasySell substitutes its merge tags ({{customer.name}}, {{order.number}}, ...)
 * into the redirect URL it sends the buyer to, so the order data arrives as
 * query parameters. Placeholders that were never substituted still contain
 * "{{", so they are treated as empty and their row is removed.
 *
 * The call promise is resolved against the store's own timezone: inside the
 * call-centre window the buyer gets a live countdown, outside it a "we will
 * call you in the morning" message.
 */
(function () {
  'use strict';

  var PARAM_ALIASES = {
    name: ['name', 'customer_name', 'full_name'],
    phone: ['phone', 'customer_phone', 'tel'],
    email: ['email', 'customer_email'],
    address1: ['address1', 'address', 'customer_address1'],
    address2: ['address2', 'customer_address2'],
    province: ['province', 'state', 'region', 'customer_province'],
    city: ['city', 'customer_city'],
    zip: ['zip', 'postcode', 'postal_code', 'customer_zip'],
    order_id: ['order_id', 'id'],
    order_number: ['order_number', 'order', 'number'],
    total: ['total', 'order_total', 'amount'],
    note: ['note', 'order_note'],
    shipping_method: ['shipping_method', 'shipping', 'delivery_method'],
    products: ['products', 'product', 'items'],
    variants: ['variants', 'variant'],
    quantity: ['quantity', 'qty'],
    variant_ids: ['variant_ids', 'variant_id'],
    products_urls: ['products_urls', 'product_urls', 'products_url']
  };

  function unresolved(value) {
    return typeof value === 'string' && value.indexOf('{{') !== -1;
  }

  function clean(value) {
    if (value == null) return '';
    var text = String(value).trim();
    if (!text || unresolved(text) || text === 'null' || text === 'undefined') return '';
    return text;
  }

  function readParams() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return {};
    }

    var data = {};
    Object.keys(PARAM_ALIASES).forEach(function (field) {
      var aliases = PARAM_ALIASES[field];
      for (var i = 0; i < aliases.length; i++) {
        var value = clean(params.get(aliases[i]));
        if (value) {
          data[field] = value;
          return;
        }
      }
    });
    return data;
  }

  /* Splits "Tea, Honey" or "Tea | Honey" into a usable list. */
  function toList(value) {
    if (!value) return [];
    return value
      .split(/\s*(?:,|\||\/{2,})\s*/)
      .map(function (item) {
        return item.trim();
      })
      .filter(Boolean);
  }

  function resolve(root) {
    var params = readParams();
    var raw = root.querySelector('[data-ss-ty-raw]');
    var data = {};

    Object.keys(PARAM_ALIASES).forEach(function (field) {
      var value = params[field] || '';

      /* Second source: merge tags EasySell rewrote straight into the markup. */
      if (!value && raw) {
        value = clean(raw.getAttribute('data-raw-' + field.replace(/_/g, '-')));
      }

      data[field] = value;
    });

    return data;
  }

  function fill(root, data) {
    root.querySelectorAll('[data-es-field]').forEach(function (node) {
      var value = data[node.getAttribute('data-es-field')] || '';
      node.textContent = value;
    });

    root.querySelectorAll('[data-es-requires]').forEach(function (node) {
      var required = node.getAttribute('data-es-requires').split(',');
      var satisfied = required.some(function (field) {
        return !!data[field.trim()];
      });
      if (!satisfied) node.remove();
    });

    root.querySelectorAll('[data-es-fallback]').forEach(function (node) {
      if (!clean(node.textContent)) node.textContent = node.getAttribute('data-es-fallback');
    });
  }

  function renderProducts(root, data) {
    var host = root.querySelector('[data-ss-ty-products]');
    if (!host) return;

    var names = toList(data.products);
    if (!names.length) {
      var wrapper = host.closest('[data-ss-ty-products-block]');
      if (wrapper) wrapper.remove();
      return;
    }

    var variants = toList(data.variants);
    var quantities = toList(data.quantity);
    var urls = toList(data.products_urls);
    var template = host.querySelector('[data-ss-ty-product-template]');
    if (!template) return;

    var markup = template.innerHTML;
    host.innerHTML = '';

    names.forEach(function (name, index) {
      var item = document.createElement('li');
      item.className = 'ss-ty__product';
      item.innerHTML = markup;

      var nameNode = item.querySelector('[data-ss-ty-product-name]');
      if (nameNode) nameNode.textContent = name;

      var variantNode = item.querySelector('[data-ss-ty-product-variant]');
      if (variantNode) {
        var variant = variants[index] || '';
        if (variant && variant !== 'Default Title') {
          variantNode.textContent = variant;
        } else {
          variantNode.remove();
        }
      }

      var qtyNode = item.querySelector('[data-ss-ty-product-qty]');
      if (qtyNode) {
        var qty = quantities[index] || quantities[0] || '';
        if (qty) {
          qtyNode.textContent = qtyNode.getAttribute('data-label').replace('{qty}', qty);
        } else {
          qtyNode.remove();
        }
      }

      var linkNode = item.querySelector('[data-ss-ty-product-link]');
      if (linkNode) {
        var url = urls[index] || '';
        if (url) {
          linkNode.setAttribute('href', url);
        } else {
          linkNode.remove();
        }
      }

      host.appendChild(item);
    });
  }

  /* Wall-clock hour and minute inside the store's timezone. */
  function localTime(timezone) {
    var now = new Date();
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      }).formatToParts(now);

      var read = function (type) {
        var found = parts.find(function (part) {
          return part.type === type;
        });
        return found ? parseInt(found.value, 10) : 0;
      };

      var hour = read('hour');
      return { hour: hour === 24 ? 0 : hour, minute: read('minute') };
    } catch (error) {
      return { hour: now.getHours(), minute: now.getMinutes() };
    }
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function formatClock(hour) {
    return pad(hour) + ':00';
  }

  function startCallWindow(root, data) {
    var box = root.querySelector('[data-ss-ty-call]');
    if (!box) return;

    var timezone = box.getAttribute('data-timezone') || 'Asia/Riyadh';
    var openHour = parseInt(box.getAttribute('data-open-hour'), 10);
    var closeHour = parseInt(box.getAttribute('data-close-hour'), 10);
    var minutes = parseInt(box.getAttribute('data-minutes'), 10) || 10;

    if (isNaN(openHour)) openHour = 10;
    if (isNaN(closeHour)) closeHour = 22;

    var headline = box.querySelector('[data-ss-ty-call-headline]');
    var note = box.querySelector('[data-ss-ty-call-note]');
    var timer = box.querySelector('[data-ss-ty-call-timer]');
    var current = localTime(timezone);
    var isOpen = current.hour >= openHour && current.hour < closeHour;

    var apply = function (state) {
      var text = box.getAttribute('data-text-' + state) || '';
      var noteText = box.getAttribute('data-note-' + state) || '';
      var clock = formatClock(openHour);
      if (headline) headline.textContent = text.replace('{minutes}', minutes).replace('{time}', clock);
      if (note) note.textContent = noteText.replace('{minutes}', minutes).replace('{time}', clock);
      box.setAttribute('data-state', state);
    };

    if (!isOpen) {
      apply(current.hour < openHour ? 'early' : 'late');
      if (timer) timer.remove();
      return;
    }

    apply('open');
    if (!timer) return;

    /* Anchored so a refresh continues the same countdown instead of restarting. */
    var key = 'ss-ty-call:' + (data.order_number || data.order_id || 'order');
    var startedAt = parseInt(sessionStorage.getItem(key), 10);
    if (!startedAt || isNaN(startedAt)) {
      startedAt = Date.now();
      try {
        sessionStorage.setItem(key, String(startedAt));
      } catch (error) {
        /* Private mode: the countdown simply restarts on refresh. */
      }
    }

    var deadline = startedAt + minutes * 60000;
    var handle = null;

    var tick = function () {
      var remaining = Math.max(0, deadline - Date.now());
      var totalSeconds = Math.round(remaining / 1000);

      if (remaining <= 0) {
        if (handle) clearInterval(handle);
        apply('due');
        timer.remove();
        return;
      }

      timer.textContent = pad(Math.floor(totalSeconds / 60)) + ':' + pad(totalSeconds % 60);
    };

    tick();
    if (timer.isConnected) handle = setInterval(tick, 1000);
  }

  function startCountry(root) {
    var box = root.querySelector('[data-ss-ty-country]');
    if (!box) return;

    var payload = box.querySelector('[data-ss-ty-countries]');
    var list = [];
    try {
      list = JSON.parse(payload ? payload.textContent : '[]');
    } catch (error) {
      list = [];
    }
    if (!Array.isArray(list) || !list.length) return;

    var nameNode = box.querySelector('[data-ss-ty-country-name]');
    var flagNode = box.querySelector('[data-ss-ty-country-flag]');

    var match = function (code) {
      var iso = String(code || '').trim().toUpperCase();
      if (!iso) return null;
      return (
        list.find(function (item) {
          return item.code === iso;
        }) || null
      );
    };

    var apply = function (code) {
      var country = match(code) || match(box.getAttribute('data-fallback')) || list[0];
      if (!country) return;
      if (nameNode) nameNode.textContent = country.name;
      if (flagNode) {
        var iso = country.code.toLowerCase();
        flagNode.src = 'https://flagcdn.com/20x15/' + iso + '.png';
        flagNode.srcset = 'https://flagcdn.com/20x15/' + iso + '.png 1x, https://flagcdn.com/40x30/' + iso + '.png 2x';
        flagNode.alt = country.name;
      }
    };

    apply(box.getAttribute('data-locale-country'));

    fetch('https://api.country.is/', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (result) {
        if (result && result.country) apply(result.country);
      })
      .catch(function () {
        /* Keep the Markets country already on screen. */
      });
  }

  function startGallery(root) {
    root.querySelectorAll('[data-ss-ty-gallery]').forEach(function (gallery) {
      var track = gallery.querySelector('[data-ss-ty-track]');
      var dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-ss-ty-dot]'));
      if (!track || dots.length < 2) return;

      var slides = Array.prototype.slice.call(track.children);
      var index = 0;

      /* Measured from the viewport so the same code works in RTL and LTR. */
      var goTo = function (next) {
        index = (next + slides.length) % slides.length;
        var slide = slides[index];
        if (slide) slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      };

      var sync = function () {
        var middle = track.getBoundingClientRect().left + track.clientWidth / 2;
        var closest = 0;
        var shortest = Infinity;

        slides.forEach(function (slide, slideIndex) {
          var rect = slide.getBoundingClientRect();
          var distance = Math.abs(rect.left + rect.width / 2 - middle);
          if (distance < shortest) {
            shortest = distance;
            closest = slideIndex;
          }
        });

        index = closest;
        dots.forEach(function (dot, dotIndex) {
          dot.classList.toggle('is-active', dotIndex === closest);
          dot.setAttribute('aria-current', dotIndex === closest ? 'true' : 'false');
        });
      };

      dots.forEach(function (dot, dotIndex) {
        dot.addEventListener('click', function () {
          goTo(dotIndex);
        });
      });

      track.addEventListener('scroll', sync, { passive: true });
      sync();

      if (gallery.getAttribute('data-autoplay') !== 'true') return;

      var delay = (parseFloat(gallery.getAttribute('data-autoplay-speed')) || 4) * 1000;
      var timer = setInterval(function () {
        goTo(index + 1);
      }, delay);

      gallery.addEventListener('pointerdown', function () {
        clearInterval(timer);
      });
    });
  }

  /* Shows only the blocks whose keywords appear in the ordered products. */
  function startProductContent(root, data) {
    var blocks = root.querySelectorAll('[data-ss-ty-when]');
    if (!blocks.length) return;

    var haystack = (data.products + ' ' + data.variants).toLowerCase();

    blocks.forEach(function (block) {
      var keywords = block
        .getAttribute('data-ss-ty-when')
        .split(',')
        .map(function (word) {
          return word.trim().toLowerCase();
        })
        .filter(Boolean);

      if (!keywords.length) {
        block.hidden = false;
        return;
      }

      var matched = haystack && keywords.some(function (word) {
        return haystack.indexOf(word) !== -1;
      });

      if (matched) {
        block.hidden = false;
      } else {
        block.remove();
      }
    });
  }

  function startCopy(root) {
    root.querySelectorAll('[data-ss-ty-copy]').forEach(function (button) {
      button.addEventListener('click', function () {
        var target = root.querySelector(button.getAttribute('data-ss-ty-copy'));
        var value = target ? target.textContent.trim() : '';
        if (!value) return;

        var done = function () {
          var original = button.getAttribute('data-label') || button.textContent;
          button.textContent = button.getAttribute('data-copied-label') || original;
          setTimeout(function () {
            button.textContent = original;
          }, 1800);
        };

        if (navigator.clipboard) {
          navigator.clipboard.writeText(value).then(done, function () {});
        }
      });
    });
  }

  /* Appends the order reference so support opens with context already filled. */
  function startContact(root, data) {
    root.querySelectorAll('[data-ss-ty-contact]').forEach(function (link) {
      var template = link.getAttribute('data-message');
      if (!template) return;

      var message = template
        .replace('{order}', data.order_number || data.order_id || '')
        .replace('{name}', data.name || '');

      var href = link.getAttribute('href') || '';
      var separator = href.indexOf('?') === -1 ? '?' : '&';
      link.setAttribute('href', href + separator + 'text=' + encodeURIComponent(message));
    });
  }

  function init(root) {
    if (!root || root.dataset.ssTyReady === 'true') return;
    root.dataset.ssTyReady = 'true';

    var data = resolve(root);
    fill(root, data);
    renderProducts(root, data);
    startCallWindow(root, data);
    startCountry(root);
    startGallery(root);
    startProductContent(root, data);
    startCopy(root);
    startContact(root, data);
  }

  function boot() {
    document.querySelectorAll('[data-ss-thank-you]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
